import React from "react";
import { ErrorType } from "../../../types/common-type";
import { useOrganizations } from "../../../core/customhook/hooks/useOrganizations";
import { useProjectsByOrgId } from "../../../core/customhook/hooks/projectHooks";
import { useLocationByProjectId } from "../../../core/customhook/hooks/locationHooks";
import { usePrograms } from "../../../core/customhook/hooks/programHooks";
import { useDepartmentByProgramId } from "../../../core/customhook/hooks/departmentHooks";
import MultiSearchableSelect from "./MultiSearchableSelect";

export interface FilterTypes {
  organization: string[];
  project: string[];
  location: string[];
  program: string[];
  department: string[];
}

export interface FilterErrors {
  organization?: string;
  project?: string;
  location?: string;
  program?: string;
  department?: string;
}

export const initialFilters: FilterTypes = {
  organization: [],
  project: [],
  location: [],
  program: [],
  department: [],
};

const DEPENDENTS: Record<keyof FilterTypes, (keyof FilterTypes)[]> = {
  organization: ["project", "location", "program", "department"],
  project: ["location", "program", "department"],
  location: ["program", "department"],
  program: ["department"],
  department: [],
};

const ALL_FIELDS: (keyof FilterTypes)[] = [
  "organization",
  "project",
  "location",
  "program",
  "department",
];

interface CommonFilterProps {
  filterData: FilterTypes;
  onFilterDataChange: (
    name: keyof FilterTypes,
    value: string[],
    labels: string[],
  ) => void;
  fields?: (keyof FilterTypes)[];
  colClass?: string | null;
  handleGetFilters?: (data: FilterTypes) => void;
  parentError?: ErrorType | FilterErrors;
  showButton?: boolean;
  buttonText?: string | null;
  onButtonClick?: () => void;
  disableButton?: boolean;
  onClearAll?: () => void;
}

const DynamicSingleSelect: React.FC<CommonFilterProps> = ({
  filterData,
  onFilterDataChange,
  fields = ALL_FIELDS,
  parentError = {},
  disableButton = false,
  showButton = false,
  buttonText = "Search",
  onButtonClick = () => {},
  onClearAll,
}) => {
  const { options: organizationOption, loading: loadingOrg } =
    useOrganizations();

  const { options: projectOptions, loading: loadingProject } =
    useProjectsByOrgId(filterData?.organization);

  const { options: locationOptions, loading: loadingLocation } =
    useLocationByProjectId(filterData?.project);

  const { options: programOptions, loading: loadingProgram } = usePrograms(
    filterData?.project,
    filterData?.location,
  );

  const { options: departmentOptions, loading: loadingDepartment } =
    useDepartmentByProgramId(filterData?.program);

  const FIELD_CONFIG = {
    organization: {
      label: "Organization",
      options: organizationOption,
      loading: loadingOrg,
    },
    project: {
      label: "Account",
      options: projectOptions,
      loading: loadingProject,
    },
    location: {
      label: "Location",
      options: locationOptions,
      loading: loadingLocation,
    },
    program: {
      label: "Program",
      options: programOptions,
      loading: loadingProgram,
    },
    department: {
      label: "Department",
      options: departmentOptions,
      loading: loadingDepartment,
    },
  };

  const isFieldDisabled = (field: keyof FilterTypes) => {
    return ALL_FIELDS?.some(
      (parent) =>
        DEPENDENTS[parent]?.includes(field) && filterData[parent]?.length === 0,
    );
  };

  const handleFieldDataChange = (
    name: string,
    values: string[],
    labels: string[],
  ) => {
    onFilterDataChange(name as keyof FilterTypes, values, labels);
  };

  const handleClearFilters = () => {
    if (onClearAll) {
      onClearAll();
      return;
    }

    ALL_FIELDS?.forEach((field) => {
      if (filterData[field]?.length > 0) {
        onFilterDataChange(field, [], []);
      }
    });
  };

  const hasActiveFilters = ALL_FIELDS?.some(
    (field) => filterData[field]?.length > 0,
  );

  const visibleFields = fields?.filter((field) => FIELD_CONFIG[field]);

  return (
    <div className="row mt-4 mt-lg-3 mb-0 gx-2 gy-0 align-items-start">
      {visibleFields?.map((field) => {
        const config = FIELD_CONFIG[field];

        const fieldError =
          filterData[field]?.length > 0 ? undefined : parentError?.[field];

        return (
          <div key={field} className="col-6 col-lg-4 mb-3">
            <MultiSearchableSelect
              label={config.label}
              name={field}
              value={filterData[field] ?? []}
              options={config.options ?? []}
              onChange={handleFieldDataChange}
              required={field === "organization" || field === "project"}
              disabled={isFieldDisabled(field)}
              loading={config.loading}
              error={typeof fieldError === "string" ? fieldError : undefined}
            />
          </div>
        );
      })}

      {showButton && (
        <div className="col-6 col-lg-4 mb-3">
          <div
            className="d-flex gap-2 align-items-end"
            style={{ minHeight: "70px" }}
          >
            <button
              type="button"
              className="btn btn-light border flex-fill"
              onClick={handleClearFilters}
              disabled={disableButton || !hasActiveFilters}
              style={{
                height: "38px",
                whiteSpace: "nowrap",
              }}
            >
              <i className="ti ti-refresh me-1" />
              Clear Filters
            </button>

            <button
              type="button"
              className="btn text-white flex-fill"
              style={{
                background: "#fe7f07",
                opacity: disableButton ? 0.7 : 1,
                height: "38px",
                whiteSpace: "nowrap",
              }}
              disabled={disableButton}
              onClick={onButtonClick}
            >
              {buttonText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicSingleSelect;
